// Considered: a bespoke checkbox/toggle pair — rejected. SegmentedControlAtom already implements
// the canonical two-state auto/pinned toggle (atomic-design.md Rule 3: toggle → SegmentedControlAtom,
// listed under this spec's "Reuse without modification" table); this molecule only adds the
// composition-ref-specific write (componentVersion) around it. See specs/space-conformance.md §
// Design System Reuse Audit.
import React from 'react';
import { useTranslation } from 'localization';
import SegmentedControlAtom, { SegmentedControlOption } from '../../atoms/SegmentedControl/SegmentedControlAtom';
import './RebasePolicyControlMolecule.scss';

export type RebasePolicy = 'auto' | 'pinned';

export interface RebasePolicyControlMoleculeProps {
  policy: RebasePolicy;
  /**
   * The referenced composition's currently resolved version (from the artifact's own lockfile) —
   * written as the ref's `componentVersion` when the user switches to `pinned`. Undefined disables
   * the control: nothing has resolved yet, so there is no version to pin to.
   */
  resolvedVersion?: number;
  /** Fires with the new policy and the `componentVersion` to write onto the ref (`resolvedVersion` when pinning, `undefined` when returning to auto). */
  onChange: (policy: RebasePolicy, componentVersion: number | undefined) => void;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

const RebasePolicyControlMolecule: React.FC<RebasePolicyControlMoleculeProps> = ({
  policy,
  resolvedVersion,
  onChange,
  disabled = false,
  className = '',
  'data-testid': testId,
}) => {
  const { t } = useTranslation();

  const options: SegmentedControlOption<RebasePolicy>[] = [
    { value: 'auto', label: t('composer.inspector.rebasePolicy.auto', { defaultValue: 'Auto' }), disabled },
    {
      value: 'pinned',
      label: t('composer.inspector.rebasePolicy.pinned', { defaultValue: 'Pinned' }),
      disabled: disabled || resolvedVersion === undefined,
    },
  ];

  const handleChange = (value: RebasePolicy | RebasePolicy[]) => {
    const next = Array.isArray(value) ? value[0] : value;
    onChange(next, next === 'pinned' ? resolvedVersion : undefined);
  };

  return (
    <div className={`rebase-policy-control ${className}`.trim()} data-testid={testId}>
      <span className="rebase-policy-control__heading">
        {t('composer.inspector.rebasePolicy.heading', { defaultValue: 'Rebase policy' })}
      </span>
      <SegmentedControlAtom<RebasePolicy>
        options={options}
        value={policy}
        onChange={handleChange}
        mode="single"
        size="small"
        ariaLabel={t('composer.inspector.rebasePolicy.heading', { defaultValue: 'Rebase policy' })}
        data-testid={testId ? `${testId}-control` : undefined}
      />
    </div>
  );
};

RebasePolicyControlMolecule.displayName = 'RebasePolicyControlMolecule';

export default RebasePolicyControlMolecule;
